import {
  Box,
  Flex,
  Button,
  Text,
  Input,
  InputGroup,
  InputLeftElement,
  IconButton,
  Icon,
} from '@chakra-ui/react';
import SearchIcon from '@/icons/Search';
import Add from '@/assets/icons/colored/Add';
import { XIcon } from '@heroicons/react/outline';
import { Function, Functions } from './components/functions';
import { Blueprints } from './components/blueprints';
import { useGetAllBluePrintQuery } from '@/store/query/blueprint';
import { Loader } from '@/design-system/loader';
import { useState } from 'react';
import debounce from 'lodash.debounce';
import { useRef } from 'react';
import { useMixpanel } from '@/hooks/use-mixpanel';

export const BluePrintList = ({
  onStartFromScratch = () => {},
  onPreviewBlueprint = () => {},
  onCreateFromBlueprint = () => {},
  onClose = () => {},
}) => {
  const { track } = useMixpanel();
  const ref = useRef();
  const [filter, setFilter] = useState('all');
  const [filteredBlueprints, setBlueprints] = useState(null);
  const { data: blueprints, isLoading } = useGetAllBluePrintQuery();

  const onSearch = debounce(() => {
    const searchTerm = ref?.current?.value;
    const searchResult = blueprints?.filter((currBp) =>
      currBp?.name?.toLocaleLowerCase()?.includes(searchTerm)
    );
    setBlueprints(searchResult);
    track({
      event: 'blueprint-search',
    });
  }, 300);

  const onFilter = (filterId) => {
    setFilter(filterId);
    if (filterId === 'all') {
      setBlueprints(null);
      return;
    }
    track({
      event: `select-${filterId}-blueprint`,
    });
    if (filterId === 'kula' || filterId === 'org') {
      const fromKula = filterId === 'kula';
      const searchResult = blueprints?.filter(
        (currBp) => currBp?.from_kula === fromKula
      );
      setBlueprints(searchResult);
      return;
    }
    const searchResult = blueprints?.filter((currBp) =>
      currBp?.tag_names.includes(filterId)
    );
    setBlueprints(searchResult);
  };

  return (
    <Box height="inherit">
      <Flex height="80px" alignItems="center" px="24px">
        <Text fontWeight="semiBold" fontSize="20px" flex="1">
          Create Flow
        </Text>
        <Box mx="8px">
          <InputGroup>
            <InputLeftElement pointerEvents="none" children={<SearchIcon />} />
            <Input
              ref={ref}
              placeholder="Search Blueprint"
              onChange={onSearch}
            />
          </InputGroup>
        </Box>
        <IconButton
          variant="icon"
          ml="16px"
          onClick={onClose}
          icon={<Icon as={XIcon} width="16px" height="16px" />}
        />
      </Flex>

      <Flex
        height="80px"
        borderColor="border-color"
        borderWidth="1px 0"
        px="24px"
        alignItems="center"
      >
        <Box>
          <Icon as={Add} width="36px" height="36px" borderRadius="8px" />
        </Box>
        <Box flex="1" ml="12px">
          <Text fontWeight="semiBold" fontSize="16px">
            Start from Scratch
          </Text>
          <Text color="#8D8D8D">
            The DIY-friendly option. Write your fresh email drip here or
            copy-paste from your notes.
          </Text>
        </Box>
        <Box>
          <Button onClick={onStartFromScratch}>Start New</Button>
        </Box>
      </Flex>

      <Flex height="calc(100% - 160px)">
        <Box
          width="280px"
          borderRight="1px"
          borderColor="border-color"
          p="24px"
        >
          <Function
            count={blueprints?.length}
            func={{ value: 'all', label: 'All Blueprints' }}
            selected={filter}
            onClick={onFilter}
          />
          <Function
            count={blueprints?.filter((bp) => bp?.from_kula)?.length}
            func={{ value: 'kula', label: 'Kula Blueprints' }}
            selected={filter}
            onClick={onFilter}
            trackingID="kula-blueprint-filter"
          />
          <Function
            count={blueprints?.filter((bp) => !bp?.from_kula)?.length}
            func={{ value: 'org', label: 'Org. Blueprints' }}
            selected={filter}
            onClick={onFilter}
            trackingID="org-blueprint-filter"
          />
          <Box mt="16px">
            <Functions
              onClick={onFilter}
              selected={filter}
              blueprints={blueprints}
            />
          </Box>
        </Box>
        <Box flex="1" p="24px">
          <Box mb="24px">
            <Text fontWeight="semiBold" fontSize="20px">
              Blueprints
            </Text>
            <Text variant="muted">
              Blueprints are ready-to-use email drips created by either Kula
              experts or shared by your colleagues.
            </Text>
          </Box>
          <Flex flexWrap="wrap" overflow="auto" height="calc(100% - 80px)">
            {isLoading ? (
              <Flex flex="1" alignItems="center" justifyContent="center">
                <Loader />
              </Flex>
            ) : (
              <Blueprints
                blueprints={filteredBlueprints ?? blueprints}
                onPreview={onPreviewBlueprint}
                onCreate={onCreateFromBlueprint}
              />
            )}
          </Flex>
        </Box>
      </Flex>
    </Box>
  );
};
